import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { errorMessage } from "../lib/errors";
import { Briefcase, MapPin, Building2, ArrowRight, Users, Sparkles, Rocket, Send, Paperclip, GraduationCap, Code, PenTool, Coins } from "lucide-react";

const JOBS = [
  { id: "j1", company: "Banque Étoile", logo: Coins, category: "Finance & Patrimoine", role: "Conseiller(ère) patrimonial junior", location: "Paris", type: "CDI", salary: "38-45k€", tags: ["débutant OK", "certif. Finance appréciée"] },
  { id: "j2", company: "GaleriaModerna", logo: PenTool, category: "Art", role: "Assistant(e) galeriste", location: "Lyon", type: "CDI", salary: "28-34k€", tags: ["certif. Histoire de l'art"] },
  { id: "j3", company: "MontreLuxe SA", logo: Sparkles, category: "Horlogerie", role: "Conseiller(ère) boutique", location: "Genève / Paris", type: "CDI", salary: "35-42k€", tags: ["certif. Horlogerie"] },
  { id: "j4", company: "PatrimoineFacile", logo: Building2, category: "Immobilier", role: "Négociateur(trice) immobilier", location: "Bordeaux", type: "CDI + commissions", salary: "25k€ + variable", tags: ["débutant OK"] },
  { id: "j5", company: "AI Studio Labs", logo: Code, category: "Technologie & IA", role: "Prompt engineer stagiaire", location: "Remote (FR)", type: "Stage 6 mois", salary: "1200€/mois", tags: ["certif. Prompt engineering"] },
  { id: "j6", company: "Coach Vitalité", logo: Rocket, category: "Sport & Santé", role: "Coach sportif indépendant", location: "Marseille", type: "Freelance", salary: "Rémunération à la séance", tags: ["cumul autorisé"] },
];

const OPEN_POSITIONS_ECS = [
  { role: "Créateur(trice) de contenu pédagogique", team: "Contenu", location: "Remote (Europe)", type: "CDI" },
  { role: "Développeur(euse) Full-Stack (React / Python)", team: "Produit", location: "Paris ou Remote", type: "CDI" },
  { role: "Designer produit senior", team: "Design", location: "Paris", type: "CDI" },
  { role: "Community & partenariats", team: "Marketing", location: "Remote (FR)", type: "CDI" },
];

const TABS = [
  { id: "jobs", label: "Offres partenaires", icon: Briefcase },
  { id: "spontaneous", label: "Candidature spontanée", icon: Send },
  { id: "ecs", label: "Rejoindre E.C.S", icon: Rocket },
];

export default function Recrutement() {
  const [tab, setTab] = useState("jobs");
  const [filter, setFilter] = useState("Tous");
  const [form, setForm] = useState({ name: "", email: "", category: "", experience: "", message: "" });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/recruitment/apply", form);
      setSent(true);
      toast.success("Candidature enregistrée — nous revenons vers vous sous 5 jours ouvrés.");
    } catch (err) {
      toast.error(errorMessage(err, "Impossible d'envoyer la candidature"));
    } finally { setBusy(false); }
  };

  const categories = ["Tous", ...Array.from(new Set(JOBS.map(j => j.category)))];
  const filteredJobs = filter === "Tous" ? JOBS : JOBS.filter(j => j.category === filter);

  return (
    <div data-testid="recrutement-page">
      {/* HERO */}
      <section className="bg-gradient-to-b from-gold-50 to-white border-b border-ink-100">
        <div className="max-w-7xl mx-auto px-6 py-16 md:py-20 grid lg:grid-cols-[1.1fr,0.9fr] gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-gold-200 text-xs font-mono text-gold-700 uppercase tracking-widest mb-6">
              <Briefcase className="w-3.5 h-3.5" /> Espace Recrutement
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-bold text-ink-900">
              Vos compétences.<br /><span className="text-gold-gradient">Une carrière qui suit.</span>
            </h1>
            <p className="mt-5 text-lg text-ink-600 max-w-xl">
              Vos certifications E.C.S ouvrent des portes. Découvrez les offres de nos entreprises partenaires,
              candidatez spontanément, ou rejoignez notre propre équipe.
            </p>
            <div className="mt-8 flex flex-wrap gap-6 text-sm text-ink-600">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-lg bg-white border border-gold-200 flex items-center justify-center">
                  <Users className="w-4 h-4 text-gold-700" />
                </div>
                <div>
                  <div className="font-semibold text-ink-900">150+ entreprises</div>
                  <div className="text-xs text-ink-500">partenaires actifs</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-lg bg-white border border-gold-200 flex items-center justify-center">
                  <GraduationCap className="w-4 h-4 text-gold-700" />
                </div>
                <div>
                  <div className="font-semibold text-ink-900">73% des certifiés</div>
                  <div className="text-xs text-ink-500">reçoivent un contact recruteur</div>
                </div>
              </div>
            </div>
          </div>

          <div className="surface rounded-2xl p-6 shadow-pop">
            <div className="text-xs uppercase tracking-widest text-gold-700 font-mono font-semibold mb-3">Comment ça marche</div>
            <ol className="space-y-4">
              {[
                "Certifiez-vous dans votre domaine (Art, Finance, Tech, Santé…)",
                "Rendez visible votre profil auprès de nos partenaires en cochant l'option dans votre compte",
                "Recevez des propositions ou candidatez aux offres publiées ci-dessous",
                "Notre équipe accompagne les mises en relation les plus qualifiées",
              ].map((step, i) => (
                <li key={i} className="flex gap-3">
                  <div className="w-7 h-7 rounded-full bg-gold-100 text-gold-700 font-semibold text-sm flex items-center justify-center flex-shrink-0">{i+1}</div>
                  <p className="text-sm text-ink-700">{step}</p>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* TABS */}
      <div className="max-w-7xl mx-auto px-6 mt-10">
        <div className="flex gap-1 border-b border-ink-200 overflow-x-auto">
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-5 py-3 text-sm font-medium whitespace-nowrap transition-colors inline-flex items-center gap-2 ${
                tab === t.id ? "text-ink-900 border-b-2 border-gold-500" : "text-ink-500 hover:text-ink-900"
              }`}
              data-testid={`recrut-tab-${t.id}`}
            >
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* TAB 1 — JOB BOARD */}
      {tab === "jobs" && (
        <section className="max-w-7xl mx-auto px-6 py-12" data-testid="tab-jobs">
          <div className="flex flex-wrap gap-2 mb-6">
            {categories.map(c => (
              <button
                key={c}
                onClick={() => setFilter(c)}
                className={`px-3 py-1.5 text-xs rounded-full border transition ${
                  filter === c
                    ? "bg-gold-500 text-white border-gold-500"
                    : "bg-white text-ink-700 border-ink-200 hover:border-gold-400"
                }`}
                data-testid={`job-filter-${c.replace(/\s+/g, '-').toLowerCase()}`}
              >{c}</button>
            ))}
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {filteredJobs.map(j => (
              <article key={j.id} className="surface rounded-2xl p-5 hover:shadow-pop transition" data-testid={`job-card-${j.id}`}>
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-11 h-11 rounded-lg bg-gold-50 border border-gold-200 flex items-center justify-center flex-shrink-0">
                    <j.logo className="w-5 h-5 text-gold-700" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-ink-900 leading-tight">{j.role}</h3>
                    <div className="text-xs text-ink-500 mt-0.5">{j.company} · <span className="text-gold-700">{j.category}</span></div>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-4 text-xs text-ink-600">
                  <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {j.location}</span>
                  <span className="inline-flex items-center gap-1"><Briefcase className="w-3.5 h-3.5" /> {j.type}</span>
                  <span className="font-semibold text-ink-900">{j.salary}</span>
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {j.tags.map(t => (
                    <span key={t} className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-ink-50 border border-ink-200 text-ink-600 font-mono">{t}</span>
                  ))}
                </div>
                <button
                  onClick={() => { setTab("spontaneous"); setForm(f => ({...f, category: j.category, message: `Bonjour,\n\nJe suis intéressé(e) par le poste « ${j.role} » chez ${j.company}.\n\n`})); toast.info("Formulaire pré-rempli pour vous."); }}
                  className="mt-4 w-full text-sm px-4 py-2 rounded-lg border border-gold-300 text-gold-700 font-medium hover:bg-gold-50 inline-flex items-center justify-center gap-1"
                  data-testid={`job-apply-${j.id}`}
                >
                  Postuler <ArrowRight className="w-4 h-4" />
                </button>
              </article>
            ))}
          </div>
          <p className="text-xs text-ink-500 mt-6 text-center">
            Vous êtes une entreprise ? <Link to="/contact" className="text-gold-700 hover:underline">Contactez-nous</Link> pour publier vos offres.
          </p>
        </section>
      )}

      {/* TAB 2 — SPONTANEOUS APPLICATION */}
      {tab === "spontaneous" && (
        <section className="max-w-3xl mx-auto px-6 py-12" data-testid="tab-spontaneous">
          <h2 className="font-display text-2xl md:text-3xl font-bold text-ink-900">Candidature spontanée</h2>
          <p className="text-ink-600 mt-2">
            Faites-vous connaître de nos entreprises partenaires. Nous étudions chaque profil et transmettons
            les candidatures pertinentes aux recruteurs concernés.
          </p>

          {sent ? (
            <div className="surface rounded-2xl p-10 mt-8 text-center" data-testid="recrut-sent">
              <div className="w-14 h-14 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-3">
                <Send className="w-7 h-7 text-success" />
              </div>
              <h3 className="font-display text-xl font-bold text-ink-900">Candidature reçue ✓</h3>
              <p className="text-ink-600 text-sm mt-2">
                Merci ! Notre équipe revient vers vous sous 5 jours ouvrés. En attendant, complétez vos certifications
                pour maximiser vos chances.
              </p>
              <Link to="/categories" className="inline-block mt-5 px-5 py-2.5 rounded-lg bg-gold-500 text-white font-medium hover:bg-gold-600">
                Voir les certifications
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="surface rounded-2xl p-6 mt-8 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-ink-700 mb-1.5 block">Nom complet</label>
                  <input required value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})}
                    className="w-full bg-white border border-ink-200 rounded-lg px-3 py-2.5 text-ink-900 focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500"
                    data-testid="recrut-name-input" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-ink-700 mb-1.5 block">Email</label>
                  <input required type="email" value={form.email} onChange={(e)=>setForm({...form, email:e.target.value})}
                    className="w-full bg-white border border-ink-200 rounded-lg px-3 py-2.5 text-ink-900 focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500"
                    data-testid="recrut-email-input" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-ink-700 mb-1.5 block">Catégorie visée</label>
                  <select required value={form.category} onChange={(e)=>setForm({...form, category:e.target.value})}
                    className="w-full bg-white border border-ink-200 rounded-lg px-3 py-2.5 text-ink-900 focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500"
                    data-testid="recrut-category-select">
                    <option value="">— Sélectionner —</option>
                    {categories.filter(c => c !== "Tous").map(c => <option key={c} value={c}>{c}</option>)}
                    <option value="Autre">Autre</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-ink-700 mb-1.5 block">Expérience</label>
                  <select required value={form.experience} onChange={(e)=>setForm({...form, experience:e.target.value})}
                    className="w-full bg-white border border-ink-200 rounded-lg px-3 py-2.5 text-ink-900 focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500"
                    data-testid="recrut-experience-select">
                    <option value="">— Sélectionner —</option>
                    <option>Débutant (0-1 an)</option>
                    <option>Junior (1-3 ans)</option>
                    <option>Confirmé (3-7 ans)</option>
                    <option>Senior (7+ ans)</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-ink-700 mb-1.5 block">Message / motivation</label>
                <textarea required minLength={20} rows={5} value={form.message} onChange={(e)=>setForm({...form, message:e.target.value})}
                  className="w-full bg-white border border-ink-200 rounded-lg px-3 py-2.5 text-ink-900 focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500"
                  placeholder="Présentez-vous en quelques lignes et indiquez ce qui vous intéresse…"
                  data-testid="recrut-message-input" />
              </div>
              <div className="rounded-lg bg-ink-50 border border-ink-200 px-4 py-3 flex items-center gap-3 text-xs text-ink-600">
                <Paperclip className="w-4 h-4 text-ink-400" />
                Fonction CV joint disponible en Phase 2. Pour l'instant, ajoutez tout lien utile dans votre message (LinkedIn, portfolio…).
              </div>
              <button type="submit" disabled={busy} className="w-full px-5 py-3 rounded-lg bg-gold-500 text-white font-semibold hover:bg-gold-600 shadow-gold disabled:opacity-60 inline-flex items-center justify-center gap-2" data-testid="recrut-submit-button">
                {busy ? "Envoi…" : <>Envoyer ma candidature <Send className="w-4 h-4" /></>}
              </button>
            </form>
          )}
        </section>
      )}

      {/* TAB 3 — JOIN E.C.S */}
      {tab === "ecs" && (
        <section className="max-w-5xl mx-auto px-6 py-12" data-testid="tab-ecs">
          <div className="grid md:grid-cols-[1fr,1.2fr] gap-8 items-start">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gold-50 border border-gold-200 text-xs font-mono text-gold-700 uppercase tracking-widest mb-4">
                <Rocket className="w-3.5 h-3.5" /> Rejoindre l'équipe
              </div>
              <h2 className="font-display text-2xl md:text-3xl font-bold text-ink-900">Construisons l'apprentissage sérieux qui donne envie</h2>
              <p className="text-ink-600 mt-3 leading-relaxed">
                E.C.S est une équipe compacte, ambitieuse et bienveillante. Nous cherchons des personnes qui partagent
                notre obsession : rendre la vraie connaissance accessible, sans jargon prétentieux.
              </p>
              <ul className="mt-5 space-y-2 text-sm text-ink-700">
                <li className="flex gap-2"><Sparkles className="w-4 h-4 text-gold-600 mt-0.5 flex-shrink-0" />Full remote possible en Europe</li>
                <li className="flex gap-2"><Sparkles className="w-4 h-4 text-gold-600 mt-0.5 flex-shrink-0" />Autonomie forte, culture d'ownership</li>
                <li className="flex gap-2"><Sparkles className="w-4 h-4 text-gold-600 mt-0.5 flex-shrink-0" />Salaire équitable + BSPCE selon poste</li>
                <li className="flex gap-2"><Sparkles className="w-4 h-4 text-gold-600 mt-0.5 flex-shrink-0" />Budget formation illimité (certifs incluses !)</li>
              </ul>
            </div>

            <div className="space-y-3">
              <div className="text-xs uppercase tracking-widest text-ink-500 font-mono font-semibold mb-2">Postes ouverts</div>
              {OPEN_POSITIONS_ECS.map((p, i) => (
                <div key={i} className="surface rounded-xl p-4 flex items-center justify-between gap-3" data-testid={`ecs-job-${i}`}>
                  <div className="min-w-0">
                    <div className="font-semibold text-ink-900">{p.role}</div>
                    <div className="text-xs text-ink-500 mt-0.5">{p.team} · {p.location} · {p.type}</div>
                  </div>
                  <button
                    onClick={() => { setTab("spontaneous"); setForm(f => ({...f, category: "E.C.S — Équipe interne", message: `Bonjour,\n\nJe candidate au poste « ${p.role} » (équipe ${p.team}).\n\n`})); toast.info("Formulaire pré-rempli."); }}
                    className="text-sm px-3 py-1.5 rounded-lg border border-gold-300 text-gold-700 font-medium hover:bg-gold-50 flex-shrink-0"
                    data-testid={`ecs-apply-${i}`}
                  >Postuler</button>
                </div>
              ))}
              <p className="text-xs text-ink-500 pt-2">
                Aucun poste ne correspond ? Envoyez-nous quand même vos motivations via la candidature spontanée.
              </p>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
